import { useEffect } from 'react'
import { useKioskStore } from '@/stores/kiosk-store'

const CHECK_INTERVAL = 5 * 60 * 1000 // check every 5 min
const INDEX_URL = '/'

/**
 * Polls `/` for a changed main JS bundle hash and auto-reloads the kiosk
 * when a new version is deployed. Vite emits hashed asset URLs into
 * index.html (e.g. `/assets/index-B5ORH5N5.js`), so we just compare the
 * filename in the served HTML against the one in the running document.
 *
 * Only reloads when the kiosk is idle (on the start screen, no active
 * order) so we don't interrupt a customer mid-order. Staff can still
 * force-update manually via the admin modal.
 *
 * Requires CloudFront to serve `/index.html` with a short TTL (or
 * `no-cache`); hashed assets stay `immutable` as usual.
 */

const ASSET_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/

function extractBundle(html: string): string | null {
  const m = html.match(ASSET_RE)
  return m ? m[0] : null
}

function currentBundle(): string | null {
  if (typeof document === 'undefined') return null
  const scripts = document.querySelectorAll<HTMLScriptElement>('script[src]')
  for (const s of scripts) {
    const m = s.src.match(ASSET_RE)
    if (m) return m[0]
  }
  return null
}

export function useAutoUpdate() {
  const _step = useKioskStore((s) => s.step)

  useEffect(() => {
    let cancelled = false
    const running = currentBundle()
    if (!running) return // dev mode (no hashed bundle) — nothing to compare

    const check = async () => {
      try {
        const res = await fetch(`${INDEX_URL}?t=${Date.now()}`, {
          cache: 'no-store',
        })
        if (!res.ok) return
        const html = await res.text()
        const served = extractBundle(html)
        if (!served || served === running) return

        // New build deployed. Only reload when kiosk is idle so we don't
        // interrupt a customer mid-order.
        const currentStep = useKioskStore.getState().step
        const isIdle = currentStep === 'attractor' || currentStep === 'menuTypeSelect'

        if (isIdle && !cancelled) {
          console.log(`[Kiosk] New build available (${served}, running ${running}) — reloading`)
          await forceUpdate()
        } else {
          console.log(`[Kiosk] New build available (${served}), will reload when idle (current step: ${currentStep})`)
        }
      } catch {
        /* network error — will retry next interval */
      }
    }

    // Check on mount, then every CHECK_INTERVAL. The effect re-runs on
    // step changes, so we also re-check whenever the kiosk goes idle.
    check()
    const id = setInterval(check, CHECK_INTERVAL)

    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])
}

export async function forceUpdate() {
  // KRITISKT: location.reload() respekterar browser-HTTP-cachen. Om HTML:en
  // är cachad serveras den och laddar samma gamla JS-bundle (hashade filer
  // är `immutable`). Vi tvingar fram en färsk request genom att byta URL
  // (add/update en query-param) — då matchar ingen cache-nyckel och browser
  // hämtar allt från nätverket.
  const url = new URL(window.location.href)
  url.searchParams.set('_v', Date.now().toString())
  window.location.replace(url.toString())
}
